import { ref } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { useToastsStore } from "@stores/toastsStore";
import type { JcdBenchmarkPayload, JcdBenchmarkWithStats } from "@/types";
import { captureEvent } from "@/lib/analytics";

export const useJcdBenchmarksStore = defineStore("jcdBenchmarks", () => {
  const benchmarks = ref<JcdBenchmarkWithStats[]>([]);
  const isLoading = ref(false);

  const toasts = useToastsStore();
  const serverErrorDetail = (error: unknown) => [error ?? []].flat().join(", ") || null;

  const fetch = async () => {
    isLoading.value = true;
    const { data } = await useMyFetch("/jcd/benchmarks").json<JcdBenchmarkWithStats[]>();
    isLoading.value = false;
    if (data.value) benchmarks.value = data.value;
    return benchmarks.value;
  };

  const create = async (payload: JcdBenchmarkPayload) => {
    const { response, error } = await useMyFetch("/jcd/benchmarks").post({ jcd_benchmark: payload }).json();
    if (response.value?.ok) {
      captureEvent("jcd_benchmark_created");
      await fetch();
    } else toasts.error("Impossible de créer le benchmark", serverErrorDetail(error.value));
    return Boolean(response.value?.ok);
  };

  const update = async (id: number, payload: JcdBenchmarkPayload) => {
    const { response, error } = await useMyFetch(`/jcd/benchmarks/${id}`).patch({ jcd_benchmark: payload }).json();
    if (response.value?.ok) {
      captureEvent("jcd_benchmark_updated", { jcd_benchmark_id: id });
      await fetch();
    } else toasts.error("Impossible d'enregistrer le benchmark", serverErrorDetail(error.value));
    return Boolean(response.value?.ok);
  };

  const duplicate = async (id: number) => {
    const { response, error } = await useMyFetch(`/jcd/benchmarks/${id}/duplicate`).post().json();
    if (response.value?.ok) {
      captureEvent("jcd_benchmark_duplicated", { jcd_benchmark_id: id });
      await fetch();
    } else toasts.error("Impossible de dupliquer le benchmark", serverErrorDetail(error.value));
    return Boolean(response.value?.ok);
  };

  const destroy = async (id: number) => {
    const { response, error } = await useMyFetch(`/jcd/benchmarks/${id}`).delete();
    if (response.value?.ok) {
      captureEvent("jcd_benchmark_deleted", { jcd_benchmark_id: id });
      benchmarks.value = benchmarks.value.filter((benchmark) => benchmark.id !== id);
    } else toasts.error("Impossible de supprimer le benchmark", serverErrorDetail(error.value));
    return Boolean(response.value?.ok);
  };

  return { benchmarks, isLoading, fetch, create, update, duplicate, destroy };
});
